// cattura-audio.ts — registra l'audio dei due (misto, mono) durante la partita, dentro l'ArchivioAudio.
// Usa un AudioWorklet (modulo creato da un Blob: nessun file in più da servire); se il browser non lo ha, il
// vecchio ScriptProcessorNode. Se l'AudioContext non parte (iOS senza un tocco dell'utente) la clip resta muta.

export interface CatturaAudio {
  readonly frequenza: number;
  ferma(): Promise<void>;
}

const CODICE_WORKLET = `
class Registratore extends AudioWorkletProcessor {
  constructor() { super(); this.buf = new Float32Array(2048); this.n = 0; }
  process(ingressi) {
    const ingresso = ingressi[0];
    if (ingresso && ingresso.length) {
      const c0 = ingresso[0];
      const c1 = ingresso[1];
      for (let i = 0; i < c0.length; i++) {
        this.buf[this.n++] = c1 ? (c0[i] + c1[i]) * 0.5 : c0[i];
        if (this.n === this.buf.length) {
          this.port.postMessage({ dati: this.buf, fine: currentFrame + i + 1 }, [this.buf.buffer]);
          this.buf = new Float32Array(2048);
          this.n = 0;
        }
      }
    }
    return true;
  }
}
registerProcessor("registratore-clip", Registratore);
`;

/**
 * Avvia la cattura. `ricevi(t, dati)` riceve blocchi mono col tempo (orologio `adesso`) del primo campione.
 * Restituisce null se l'audio non è disponibile (nessuna traccia o contesto bloccato).
 */
export async function avviaCatturaAudio(
  flussi: MediaStream[],
  adesso: () => number,
  ricevi: (t: number, dati: Float32Array) => void,
): Promise<CatturaAudio | null> {
  const conAudio = flussi.filter((f) => f.getAudioTracks().some((t) => t.readyState === "live"));
  if (!conAudio.length || typeof AudioContext === "undefined") return null;
  let ctx: AudioContext;
  try {
    ctx = new AudioContext({ latencyHint: "playback" });
  } catch {
    return null;
  }
  try {
    if (ctx.state !== "running") await Promise.race([ctx.resume(), new Promise((r) => setTimeout(r, 500))]);
  } catch {
    // resta sospeso: si controlla sotto
  }
  if (ctx.state !== "running") {
    void ctx.close().catch(() => undefined);
    return null;
  }
  const frequenza = ctx.sampleRate;
  const misto = ctx.createGain();
  // Le sorgenti si creano su flussi con le sole tracce audio (così non si tocca il video dell'app).
  const sorgenti = conAudio.map((f) => ctx.createMediaStreamSource(new MediaStream(f.getAudioTracks())));
  for (const s of sorgenti) s.connect(misto);
  // Riferimento tra l'orologio dell'audio e quello dell'app.
  const t0 = adesso();
  const ct0 = ctx.currentTime;
  const tempoDi = (fotogrammaFine: number, n: number) => t0 + ((fotogrammaFine - n) / frequenza - ct0) * 1000;
  let nodo: AudioNode;
  let muto: GainNode | null = null;
  let url: string | null = null;
  try {
    if (!ctx.audioWorklet) throw new Error("senza worklet");
    url = URL.createObjectURL(new Blob([CODICE_WORKLET], { type: "text/javascript" }));
    await ctx.audioWorklet.addModule(url);
    const w = new AudioWorkletNode(ctx, "registratore-clip", { numberOfInputs: 1, numberOfOutputs: 1, channelCount: 2, channelCountMode: "explicit" });
    w.port.onmessage = (e: MessageEvent<{ dati: Float32Array; fine: number }>) => ricevi(tempoDi(e.data.fine, e.data.dati.length), e.data.dati);
    nodo = w;
  } catch {
    // Ripiego: ScriptProcessorNode (deprecato ma presente ovunque).
    const sp = ctx.createScriptProcessor(2048, 2, 1);
    let campioni = Math.round(ct0 * frequenza);
    sp.onaudioprocess = (e) => {
      const c0 = e.inputBuffer.getChannelData(0);
      const c1 = e.inputBuffer.numberOfChannels > 1 ? e.inputBuffer.getChannelData(1) : null;
      const out = new Float32Array(c0.length);
      for (let i = 0; i < c0.length; i++) out[i] = c1 ? ((c0[i] ?? 0) + (c1[i] ?? 0)) * 0.5 : (c0[i] ?? 0);
      campioni += c0.length;
      ricevi(tempoDi(campioni, c0.length), out);
    };
    nodo = sp;
  }
  misto.connect(nodo);
  // Il nodo deve arrivare alla destinazione per essere elaborato, ma senza far sentire nulla.
  muto = ctx.createGain();
  muto.gain.value = 0;
  nodo.connect(muto);
  muto.connect(ctx.destination);
  return {
    frequenza,
    async ferma() {
      for (const s of sorgenti) s.disconnect();
      misto.disconnect();
      nodo.disconnect();
      muto?.disconnect();
      if (url) URL.revokeObjectURL(url);
      await ctx.close().catch(() => undefined);
    },
  };
}
